/**
 * App launcher — move between the modules a workspace owns.
 *
 * Phase 3 Package 3 (.claude/specs/module-ui-rollout.md AC7-AC9).
 *
 * Deliberately a launcher, not a picker on every login. Most users only ever
 * touch one module — an employee has only Core, a recruiter only Recruitment —
 * so a mandatory chooser would tax every session for a choice they never make.
 * This is the Google Workspace pattern: land in an app, switch via a grid icon.
 *
 * MOUNTED TWICE, ON PURPOSE. responsive.css sets
 * `.shell-header { display: none !important }` below 768px, which also hides
 * notifications and the avatar. A launcher mounted only in the header is
 * invisible on phones. The second mount lives in AppNav's mobile top bar. An
 * earlier attempt at this feature hit exactly that and had to do the same — do
 * not "simplify" it back to one instance without checking a 375px viewport.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  LANDING_PREFERENCES, LandingPreference,
  getLandingPreference, setLandingPreference,
} from "../landingPreference";

import {
  CORE_LABEL,
  MODULE_KEYS, MODULE_LABELS, MODULE_COLORS, ModuleKey, ModuleMap, ownsModule,
  MODULE_HOME_PATHS,
} from "../moduleMetadata";

const GLYPH: Record<ModuleKey, string> = {
  recruitment: "◈",
  finance: "◆",
  payroll: "▣",
  performance: "▲",
  ai_expense_capture: "◇",
  esign: "✎",
};

interface Props {
  modules: ModuleMap | undefined;
  /**
   * Modules the current ROLE can use (spec: module-launcher-role-scope).
   * Entitlements are tenant-wide, so without this filter an employee is
   * offered Payroll and lands on an operator dashboard that 403s. Applies to
   * upsell tiles too — a role that could never use a module is not its buyer.
   * Undefined = no role filter (legacy callers).
   */
  eligible?: Set<ModuleKey>;
  /** Whose preference to read and write. */
  userId?: number | string;
  activeModule: ModuleKey | null;
  /** Which side the panel hangs from — the mobile bar sits at the left. */
  anchor?: "left" | "right";
  /** Rendered inside the dark mobile bar, so the trigger needs light-on-dark. */
  dark?: boolean;
  onNavigate?: () => void;
}

export default function ProductSwitcher({
  modules, eligible, activeModule, userId, anchor = "right", dark = false, onNavigate,
}: Props) {
  const [open, setOpen] = useState(false);
  const [landing, setLanding] = useState<LandingPreference>(() => getLandingPreference(userId));
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const usable = (k: ModuleKey) => !eligible || eligible.has(k);
  const owned = MODULE_KEYS.filter((k) => ownsModule(modules, k) && usable(k));
  const unowned = MODULE_KEYS.filter((k) => !ownsModule(modules, k) && usable(k));

  // A chooser with one destination is noise. Users who can only ever reach Core
  // — employees, contractors — get no launcher at all rather than a single tile.
  if (owned.length === 0) return null;

  function go(to: string) {
    setOpen(false);
    onNavigate?.();
    navigate(to);
  }

  return (
    <div ref={wrapRef} style={{ position: "relative", flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Switch module"
        title="Switch module"
        style={{
          // 44px, not 40: the design system's minimum tap target below 768px,
          // and this trigger is the ONLY way to switch modules on a phone.
          // Measured at 375px after Package 3 shipped at 40px.
          width: 44, height: 44, minWidth: 44, minHeight: 44,
          display: "grid", placeItems: "center",
          borderRadius: 8, cursor: "pointer",
          background: open ? (dark ? "rgba(255,255,255,.12)" : "#eff6ff") : "transparent",
          border: `1px solid ${open ? (dark ? "rgba(255,255,255,.2)" : "#93c5fd") : "transparent"}`,
          color: dark ? "#cbd5e1" : "#475569",
          lineHeight: 1,
        }}
      >
        {/* 3×3 launcher grid (Google Workspace pattern); currentColor keeps it
            legible on both the light header and the dark mobile bar. */}
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden focusable="false">
          {[2, 9, 16].map((cy) =>
            [2, 9, 16].map((cx) => (
              <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="1.7" fill="currentColor" />
            ))
          )}
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: "absolute", top: 46, [anchor]: 0, zIndex: 400,
            width: 296, maxWidth: "calc(100vw - 32px)",
            background: "white", border: "1px solid #e2e8f0", borderRadius: 14,
            boxShadow: "0 8px 28px rgba(30,58,138,.10)", padding: 12,
          }}
        >
          <div style={{
            fontSize: 10, fontWeight: 700, textTransform: "uppercase",
            letterSpacing: ".1em", color: "#64748b", padding: "2px 4px 9px",
          }}>
            Your modules
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
            <Tile
              label={CORE_LABEL}
              glyph="◉"
              color="#1e3a8a"
              active={activeModule === null}
              onClick={() => go("/")}
            />
            {owned.map((key) => (
              // Keyed Fragment rather than key on <Tile>: this project's React
              // typings reject `key` as a prop on a function component, the same
              // quirk that ruled out mapped <Route key=...> in Package 1.
              <React.Fragment key={key}>
                <Tile
                  label={MODULE_LABELS[key]}
                  glyph={GLYPH[key]}
                  color={MODULE_COLORS[key]}
                  active={activeModule === key}
                  onClick={() => go(MODULE_HOME_PATHS[key])}
                />
              </React.Fragment>
            ))}
            {unowned.map((key) => (
              // Keyed Fragment rather than key on <Tile>: this project's React
              // typings reject `key` as a prop on a function component, the same
              // quirk that ruled out mapped <Route key=...> in Package 1.
              <React.Fragment key={key}>
                <Tile
                  label={MODULE_LABELS[key]}
                  glyph={GLYPH[key]}
                  color={MODULE_COLORS[key]}
                  locked
                  onClick={() => go(MODULE_HOME_PATHS[key])}
                />
              </React.Fragment>
            ))}
          </div>

          {unowned.length > 0 && (
            <div style={{
              marginTop: 10, paddingTop: 9, borderTop: "1px solid #eef2f6",
              fontSize: 10.5, color: "#64748b",
            }}>
              {unowned.length} module{unowned.length === 1 ? "" : "s"} not on your plan
            </div>
          )}

          {/*
            The landing preference lives here rather than in Settings, which the
            spec suggested. Only Admin/Assistant and HR shells have a /settings
            route -- Finance and Recruiter, who are exactly the multi-module
            users this is for, cannot reach it. A preference its own audience
            cannot open is not a preference. This panel is reachable by everyone
            who has modules to switch between.
          */}
          <div style={{ marginTop: 10, paddingTop: 9, borderTop: "1px solid #eef2f6" }}>
            <label
              htmlFor="landing-pref"
              style={{
                display: "block", fontSize: 10, fontWeight: 700,
                textTransform: "uppercase", letterSpacing: ".1em",
                color: "#64748b", marginBottom: 5,
              }}
            >
              On login, open
            </label>
            <select
              id="landing-pref"
              value={landing}
              onChange={(e) => {
                const next = e.target.value as LandingPreference;
                setLanding(next);
                setLandingPreference(userId, next);
              }}
              style={{
                width: "100%", minHeight: 44, padding: "8px 10px",
                border: "1px solid #e2e8f0", borderRadius: 6,
                fontSize: 13, color: "#0f172a", background: "white",
              }}
            >
              {LANDING_PREFERENCES.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

/** Named rather than inline: an inline destructured prop type makes TS check
 *  `key` as an ordinary prop, which it is not. */
interface TileProps {
  label: string;
  sub?: string;
  glyph: string;
  color: string;
  active?: boolean;
  locked?: boolean;
  onClick: () => void;
}

function Tile({ label, sub, glyph, color, active = false, locked = false, onClick }: TileProps) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      // Locked tiles are still buttons: clicking one opens its upsell page
      // (Package 5), which is a revenue surface, not an error state.
      aria-label={locked ? `${label} — not on your plan` : label}
      style={{
        position: "relative",
        display: "block", width: "100%", minHeight: 72,
        border: `1px solid ${active ? "#38bdf8" : "#e2e8f0"}`,
        background: active ? "rgba(56,189,248,.10)" : "#f8fafc",
        borderRadius: 10, padding: "10px 5px", cursor: "pointer", textAlign: "center",
      }}
    >
      <span style={{
        width: 26, height: 26, borderRadius: 7, margin: "0 auto 6px",
        display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700,
        background: locked ? "#f1f5f9" : color,
        color: locked ? "#64748b" : "white",
      }}>
        {glyph}
      </span>
      <span style={{
        display: "block", fontSize: 10.5, fontWeight: 650, lineHeight: 1.25,
        color: locked ? "#64748b" : "#0f172a",
      }}>
        {label}
        {sub && <><br />{sub}</>}
      </span>
      {locked && (
        <span aria-hidden style={{ position: "absolute", top: 4, right: 5, fontSize: 8 }}>🔒</span>
      )}
    </button>
  );
}
